<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div
            class="tw-mx-auto tw-w-[95%] md:tw-max-w-[800px] tw-h-fit tw-overflow-auto tw-flex tw-flex-col tw-border tw-border-solid dark:tw-border-gray-400 tw-border-gray-300 tw-shadow-md tw-my-16 tw-rounded">
            <div>
                <div
                    class="tw-p-4 tw-text-lg tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-solid tw-font-semibold dark:tw-text-gray-700 tw-text-gray-500 dark:tw-bg-gray-100 tw-bg-gray-50">
                    <span>{{ $t("common.sourcing_request") }}</span>
                    <span class="tw-text-primary-500 tw-bg-white tw-py-0 tw-rounded tw-px-2 tw-border tw-border-solid tw-border-gray-200 tw-ms-auto">{{ sourcing.id }}</span>
                </div>

                <div class="tw-p-5 tw-bg-white tw-flex-1">
                    <div class="tw-grid tw-grid-cols-12 tw-gap-x-4 tw-gap-y-0">
                        <div class="tw-col-span-12 md:tw-col-span-12 tw-pb-4">
                            <h1 class="tw-ps-4 tw-border-s-2 tw-border-solid tw-border-primary-500">
                                {{ $t('common.sourcing_type') }}: 
                                <span class="tw-text-primary-500">{{ $t('sourcing.types.' + sourcing.sourcing_type) }}</span>
                            </h1>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12 tw-pb-4">
                            <h1 class="tw-ps-4 tw-border-s-2 tw-border-solid tw-border-primary-500">{{
                                $t('common.sourcing_informations') }}</h1>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField v-model="sourcing.product_name" :disabled="sourcing.sourcing_type === 'restock'" :label="$t('fields.product_name')" type="text"
                                :error="errors.product_name && $t('errors.' + errors.product_name, {
                                    field: $t('fields.product_name'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6" v-if="!hasVariants">
                            <FormField v-model.number="sourcing.quantity" :label="$t('fields.quantity')" type="number" :error="errors.quantity && $t('errors.' + errors.quantity, {
                                field: $t('fields.quantity'),
                            })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormVueSelect v-model="sourcing.destination_country" max-height="200" option-label="name"
                                option-value="value" :options="countries" :label="$t('fields.destination_country')"
                                type="text" :error="errors.destination_country && $t('errors.' + errors.destination_country, {
                                    field: $t('fields.destination_country'),
                                })">
                                <template v-slot:option="{ option }">
                                    <div>
                                        <p class="tw-max-w-[135px] tw-truncate tw-text-xs">{{ option.name }}</p>
                                    </div>
                                </template>
                            </FormVueSelect>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormVueSelect v-model="sourcing.shipping_method" max-height="200" option-label="name"
                                option-value="value" :options="shipping_methods" :label="$t('fields.shipping_method')"
                                type="text" :error="errors.shipping_method && $t('errors.' + errors.shipping_method, {
                                    field: $t('fields.shipping_method'),
                                })">
                                <template v-slot:option="{ option }">
                                    <div>
                                        <p class="tw-max-w-[135px] tw-truncate tw-text-xs">{{ option.name }}</p>
                                    </div>
                                </template>
                            </FormVueSelect>
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <FormField left-icon="solar:link-bold-duotone" v-model="sourcing.product_url"
                                :label="$t('fields.product_url')" type="text" :error="errors.product_url && $t('errors.' + errors.product_url, {
                                    field: $t('fields.product_url'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <SourcingStatus v-model:sourcing="sourcing" :errors="errors" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="solar:tag-price-bold-duotone" v-model.number="sourcing.cost_per_unit"
                                :label="$t('fields.cost_per_unit')" type="number" :error="errors.cost_per_unit && $t('errors.' + errors.cost_per_unit, {
                                    field: $t('fields.cost_per_unit'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="ic:twotone-local-shipping" v-model.number="sourcing.shipping_cost"
                                :label="$t('fields.shipping_cost')" type="number" :error="errors.shipping_cost && $t('errors.' + errors.shipping_cost, {
                                    field: $t('fields.shipping_cost'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="hugeicons:money-add-02" v-model.number="sourcing.additional_fees"
                                :label="$t('fields.additional_fees')" type="number" :error="errors.additional_fees && $t('errors.' + errors.additional_fees, {
                                    field: $t('fields.additional_fees'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="material-symbols:weight-outline" v-model.number="sourcing.weight"
                                :label="$t('fields.weight') + ' (KG)'" type="number" :error="errors.weight && $t('errors.' + errors.weight, {
                                    field: $t('fields.weight'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField disabled left-icon="solar:tag-price-bold-duotone" v-model="buyingPrice" 
                                :label="$t('fields.buying-price')" type="number" :error="errors.buying_price && $t('errors.' + errors.buying_price, {
                                    field: $t('fields.buying-price'),
                                })" />
                        </div>
                        
                        <div class="tw-col-span-12 md:tw-col-span-6">
                            <FormField left-icon="solar:tag-price-bold-duotone" v-model.number="sourcing.selling_price" 
                                :label="$t('fields.selling-price')" type="number" :error="errors.selling_price && $t('errors.' + errors.selling_price, {
                                    field: $t('fields.selling-price'),
                                })" />
                        </div>

                        <div class="tw-col-span-12 md:tw-col-span-12">
                            <FormField v-model="sourcing.note" :label="$t('fields.note')" type="textarea" :error="errors.note && $t('errors.' + errors.note, {
                                field: $t('fields.note'),
                            })" />
                        </div>

                        <div v-if="hasVariants" class="tw-col-span-12 md:tw-col-span-12 tw-pb-4">
                            <h1 class="tw-ps-4 tw-border-s-2 tw-border-solid tw-border-primary-500">{{
                                $t('common.sourcing_variants') }}</h1>
                        </div>

                        <div v-if="hasVariants && sourcing.sourcing_type === 'new_product'" class="tw-col-span-12 md:tw-col-span-12">
                            <SourcingVariants v-model:variants="sourcing.variants" />
                        </div>

                        <div v-if="sourcing.sourcing_type === 'restock'" class="tw-col-span-12 md:tw-col-span-12">
                            <div v-if="!hasVariants" class="tw-bg-amber-50 tw-p-3 tw-rounded tw-mb-3 tw-text-amber-700 tw-text-sm">
                                {{ $t('common.no_variants_for_restock') }}
                            </div>
                            <div v-else class="tw-rounded-lg tw-overflow-hidden tw-border tw-border-gray-200">
                                <table class="tw-w-full tw-border-collapse tw-table-auto">
                                    <thead class="tw-bg-gray-50">
                                        <tr>
                                            <th class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase">
                                                {{ $t('fields.variant_name') }}
                                            </th>
                                            <th class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200 tw-text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-w-24">
                                                {{ $t('fields.quantity') }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(variant, index) in sourcing.variants" :key="variant.id || index">
                                            <td class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200">
                                                <span>{{ variant.variant_name }}</span>
                                            </td>
                                            <td class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-200">
                                                <FormField 
                                                    v-model.number="variant.quantity" 
                                                    type="number" 
                                                    hideLabel 
                                                    hideErrors
                                                    class="tw-w-full quantity-input"
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="tw-p-4 tw-text-lg tw-border-t tw-justify-end tw-border-solid tw-font-medium tw-flex dark:tw-text-gray-700 tw-text-gray-500 tw-items-center tw-gap-4 dark:tw-bg-gray-100 tw-bg-gray-50">
                    <a-button :disabled="loading" @click="onCancel" class="tw-min-w-[120px]" intent="simple">{{
                        $t("buttons.cancel") }}</a-button>
                    <a-button :disabled="loading" :loading="loading" @click="onUpdate" class="tw-min-w-[120px]">{{
                        $t("buttons.update") }}</a-button>
                </div>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, computed } from 'vue';
import { clone } from '@/utils/methods';
import { update } from '@/api/sourcings';
import { useI18n } from 'vue-i18n';
import useAlert from '@/composables/useAlert';
import FormField from "@/components/form/FormField";
import FormVueSelect from "@/components/form/FormVueSelect";
import SourcingStatus from './partials/SourcingStatus.vue';
import SourcingVariants from './partials/SourcingVariants.vue';
import { countries as shipping_countries, shipping_methods as shipping_ways } from '@/config/sourcing';

const i18n = useI18n()

const emit = defineEmits(['update:visible', 'update']);
const props = defineProps({
    item: {
        required: true
    },
    visible: {
        required: true,
        default: false
    }
});

const sourcing = ref(clone(props.item));
const originalSourcing = ref(clone(props.item));
const countries = computed(() => shipping_countries.map(c => ({ ...c, name: i18n.t('countries.' + c.value) })))
const shipping_methods = computed(() => shipping_ways.map(c => ({ ...c, name: i18n.t('sourcing.shipping_methods.' + c.value) })))
const errors = ref({})
const changed = ref([])
const loading = ref(false)

// Check if the sourcing has variants
const hasVariants = computed(() => {
    return sourcing.value.variants && Array.isArray(sourcing.value.variants) && sourcing.value.variants.length > 0;
});

// Calculate buying price based on cost_per_unit, shipping_cost, and quantity
const buyingPrice = computed(() => {
    if (!(sourcing.value.shipping_cost > 0 && sourcing.value.quantity > 0)) {
        return sourcing.value.cost_per_unit;
    }
    const costPerUnit = sourcing.value.cost_per_unit ?? 0;
    const shippingAndFees = (sourcing.value.shipping_cost + (sourcing.value.additional_fees || 0)) / sourcing.value.quantity;
    return Number(costPerUnit + shippingAndFees).toFixed(2);
});

watch(() => props.item, (newValue) => {
    sourcing.value = clone(newValue)
    originalSourcing.value = clone(newValue)
    
    // Ensure variants are properly initialized
    if (!sourcing.value.variants || !Array.isArray(sourcing.value.variants)) {
        sourcing.value.variants = [];
        console.log("Initialized variants array");
    } else {
        console.log(`Sourcing has ${sourcing.value.variants.length} variants`);
    }
}, { deep: true })

// Watch for changes in variant quantities to update total quantity
watch(() => sourcing.value.variants, () => {
    if (hasVariants.value) {
        // Calculate total quantity from variants
        sourcing.value.quantity = sourcing.value.variants.reduce((total, variant) => {
            return total + (parseInt(variant.quantity) || 0);
        }, 0);
    }
}, { deep: true });

const onUpdate = async () => {
    // Calculate quantity from variants if needed
    if (hasVariants.value) {
        sourcing.value.quantity = sourcing.value.variants.reduce((total, variant) => {
            return total + (parseInt(variant.quantity) || 0);
        }, 0);
    }
    
    const fields = getChangedFields()
    
    // Add buying_price to the data before sending to API
    const data = { 
        ...sourcing.value, 
        fields,
        buying_price: buyingPrice.value
    }

    loading.value = true
    await update(props.item.id, data)
        .then(
            res => {
                if (res.data.code == 'SUCCESS') {
                    emit('update:visible', false)
                    emit('update', res.data.sourcing)
                    useAlert(i18n.t('messages.updated-successfully', { field: i18n.t('common.sourcing') }))
                }
            },
            (err) => {
                errors.value = {}
                if (err?.response?.status == 422) {
                    for (let e in err.response.data.errors) {
                        errors.value[e] = err.response.data.errors[e][0];
                    }
                }
            }
        )
    loading.value = false
}

const onCancel = () => {
    emit('update:visible', false)
}

const getChangedFields = () => {
    changed.value = []
    
    // Check regular fields
    for (const key in sourcing.value) {
        if (key === 'variants') continue; // Skip variants array as it's handled separately
        
        if (JSON.stringify(sourcing.value[key]) !== JSON.stringify(originalSourcing.value[key])) {
            changed.value.push(key)
        }
    }
    
    // Check for changes in variants
    if (hasVariants.value) {
        // Compare each variant's fields
        let variantsChanged = false;
        
        // First, check if the number of variants has changed
        if (sourcing.value.variants.length !== originalSourcing.value.variants.length) {
            variantsChanged = true;
        } else {
            // Check if any variant properties have changed
            for (let i = 0; i < sourcing.value.variants.length; i++) {
                if (
                    sourcing.value.variants[i].variant_name !== originalSourcing.value.variants[i].variant_name ||
                    sourcing.value.variants[i].quantity !== originalSourcing.value.variants[i].quantity ||
                    sourcing.value.variants[i].product_variant_id !== originalSourcing.value.variants[i].product_variant_id
                ) {
                    variantsChanged = true;
                    break;
                }
            }
        }
        
        if (variantsChanged) {
            changed.value.push('variants');
            
            // If quantity is calculated from variants, add it to changed fields
            if (!changed.value.includes('quantity')) {
                changed.value.push('quantity');
            }
        }
    }
    
    return changed.value
}
</script>

<style></style>